import { Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { FileText, ArrowRight, Sparkles } from "lucide-react";

const TOOLS = [
  {
    key: "volunteer-application",
    title: "Volunteer Application Letter",
    titleHi: "स्वयंसेवक आवेदन पत्र",
    description:
      "Generate a ready-to-print Hindi application letter for volunteer / police verification at Ujjain Simhastha 2028. Fill the form and download a personalised PDF instantly.",
    bullets: [
      "Pre-formatted Hindi (Devanagari) letter",
      "Download filled PDF + blank PDF",
      "Auto-emailed to applicant",
    ],
    to: "/tools/volunteer-application",
    accent: "#FF9933",
    icon: FileText,
  },
];

export default function Tools() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Pilgrim Tools — Ujjain Maha Kumbh 2028";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta)
      meta.setAttribute(
        "content",
        "Free pilgrim tools for Ujjain Simhastha 2028: Hindi volunteer application letter PDF generator, blank printable forms, and more utilities.",
      );
    return () => {
      document.title = prev;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);
  return (
    <div
      className="min-h-screen pt-32 pb-20"
      style={{ background: "#1c1917" }}
      data-testid="tools-page"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-saffron/10 border border-saffron/20 mb-5">
            <Sparkles size={14} className="text-saffron" />
            <span className="text-saffron text-xs font-body uppercase tracking-widest">
              Tools & Utilities
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-tight mb-4">
            Pilgrim Tools
          </h1>
          <p className="text-white/60 text-base sm:text-lg font-body max-w-2xl leading-relaxed">
            Handy utilities to prepare for Ujjain Maha Kumbh 2028 — generate
            official application templates, download blank forms, and more.
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TOOLS.map((tool, idx) => {
            const Icon = tool.icon;
            return (
              <motion.div
                key={tool.key}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 + idx * 0.1 }}
              >
                <Link
                  to={tool.to}
                  data-testid={`tool-card-${tool.key}`}
                  className="group block h-full rounded-2xl p-6 border border-white/10 hover:border-saffron/40 transition-all duration-300 hover:-translate-y-1"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,153,51,0.04) 0%, rgba(28,28,30,0.6) 60%)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{
                      background: `${tool.accent}1A`,
                      border: `1px solid ${tool.accent}33`,
                    }}
                  >
                    <Icon size={22} style={{ color: tool.accent }} />
                  </div>

                  <h3 className="font-heading text-white font-semibold text-xl mb-1">
                    {tool.title}
                  </h3>
                  <p
                    className="text-sm mb-4"
                    style={{
                      fontFamily:
                        "'Noto Sans Devanagari', 'Noto Serif Devanagari', sans-serif",
                      color: "rgba(255,255,255,0.55)",
                    }}
                  >
                    {tool.titleHi}
                  </p>

                  <p className="text-white/60 text-sm font-body leading-relaxed mb-5">
                    {tool.description}
                  </p>

                  <ul className="space-y-1.5 mb-6">
                    {tool.bullets.map((b) => (
                      <li
                        key={b}
                        className="text-white/50 text-xs font-body flex items-start gap-2"
                      >
                        <span
                          className="inline-block w-1 h-1 rounded-full mt-1.5 flex-shrink-0"
                          style={{ background: tool.accent }}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center gap-2 text-saffron text-sm font-body font-medium group-hover:gap-3 transition-all">
                    Open Tool
                    <ArrowRight size={16} />
                  </div>
                </Link>
              </motion.div>
            );
          })}

          {/* Coming Soon placeholder card */}
          <div
            className="rounded-2xl p-6 border border-dashed border-white/10 flex flex-col items-center justify-center text-center min-h-[280px]"
            style={{ background: "rgba(255,255,255,0.02)" }}
            data-testid="tool-coming-soon-card"
          >
            <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-4">
              <Sparkles size={22} className="text-white/30" />
            </div>
            <h3 className="font-heading text-white/70 font-semibold text-lg mb-1">
              More tools coming soon
            </h3>
            <p className="text-white/40 text-xs font-body">
              Travel checklist, accommodation booking and more.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
